import { useRef, useState, DragEvent, ChangeEvent } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  styled,
} from '@mui/material';
import { Upload } from '~/assets/icons';
import { theme } from '~/shared/styles/theme';
import { useCloudinaryUpload } from '~/shared/hooks/useCloudinaryUpload';

interface Props {
  value: string | null;
  onChange: (url: string | null) => void;
}

const ProfileImageUpload = ({ value, onChange }: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const { error, uploading, uploadProgress, upload, clearError } =
    useCloudinaryUpload();

  const handleFileSelection = async (file: File) => {
    setFileName(file.name);

    try {
      const uploadedUrl = await upload(file);
      onChange(uploadedUrl);
    } catch {
      // Error is already managed by the hook
      setFileName(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileSelection(files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleClearImage = () => {
    onChange(null);
    setFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    clearError();
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelection(files[0]);
    }
  };

  const renderUploadContent = () => {
    if (value && !uploading) {
      return (
        <FileNameContainer>
          <FileName variant="body2">{fileName}</FileName>
          <ClearIconButton
            onClick={handleClearImage}
            aria-label="Clear image"
            type="button"
          >
            ×
          </ClearIconButton>
        </FileNameContainer>
      );
    }

    if (uploading) {
      return (
        <UploadingContainer>
          <CircularProgress
            variant="determinate"
            value={uploadProgress}
            size={60}
          />
          <ProgressText variant="body2">{uploadProgress}%</ProgressText>
        </UploadingContainer>
      );
    }

    return (
      <UploadContent>
        <SelectButton
          variant="outlined"
          onClick={handleClick}
          disabled={uploading}
          startIcon={<Upload />}
        >
          Select a file
        </SelectButton>
        <OrText variant="body2">or</OrText>
        <DragText variant="body2">Drag and drop a file here</DragText>
      </UploadContent>
    );
  };

  return (
    <Container>
      <Label variant="body2">Profile Image</Label>
      <UploadArea
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        hasValue={Boolean(value)}
        isDragging={isDragging}
      >
        {renderUploadContent()}
        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleFileInputChange}
          disabled={uploading}
          data-testid="file-input"
        />
      </UploadArea>
      {error && <ErrorText data-testid="upload-error">{error}</ErrorText>}
    </Container>
  );
};

export default ProfileImageUpload;

const Container = styled(Box)({
  width: '100%',
});

const Label = styled(Typography)({
  color: theme.palette.custom.textTitle,
  marginBottom: '4px',
});

const UploadArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasValue' && prop !== 'isDragging',
})<{ hasValue: boolean; isDragging: boolean }>(({ hasValue, isDragging }) => {
  let backgroundColor = theme.palette.common.white;
  if (isDragging && !hasValue) {
    backgroundColor = theme.palette.custom.cream;
  }

  return {
    position: 'relative',
    width: '100%',
    minHeight: '145px',
    borderRadius: '14px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    transition: 'all 0.2s ease',
    cursor: hasValue ? 'default' : 'pointer',
    overflow: 'hidden',
    border: hasValue
      ? 'none'
      : `1px solid ${theme.palette.custom.lightGrayBrown}`,
    backgroundColor,
    boxShadow: hasValue ? 'none' : '0 11px 14px -14px rgba(2, 43, 73, 0.25)',
  };
});

const UploadContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
});

const SelectButton = styled(Button)({
  fontSize: '13px',
  height: '36px',
  padding: '10px 14px',
  borderRadius: '28px',
  boxShadow: '0 3px 10px -3px rgba(0, 0, 0, 0.12)',
  border: 'solid 1px #79553a',
  backgroundColor: theme.palette.custom.cream,
  color: theme.palette.custom.darkBrown,
  '&:hover': {
    backgroundColor: theme.palette.custom.cream,
    borderColor: '#79553a',
  },
});

const OrText = styled(Typography)({
  color: theme.palette.custom.textDescription,
  fontSize: '10px',
});

const DragText = styled(Typography)({
  color: theme.palette.custom.textDescription,
  fontSize: '10px',
});

const HiddenInput = styled('input')({
  display: 'none',
});

const FileNameContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
});

const FileName = styled(Typography)({
  color: theme.palette.custom.textDescription,
  fontSize: '14px',
});

const ClearIconButton = styled('button')({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px 6px',
  fontSize: '18px',
  color: theme.palette.custom.textDescription,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  '&:hover': {
    color: theme.palette.custom.darkBrown,
  },
});

const UploadingContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
});

const ProgressText = styled(Typography)({
  color: theme.palette.custom.textDescription,
  fontSize: '14px',
  fontWeight: 500,
});

const ErrorText = styled(Typography)({
  color: theme.palette.error.main,
  fontSize: '12px',
  marginTop: theme.spacing(1),
});
